/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Sparkles, CheckCircle2, Clock, AlertTriangle, Loader2, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const CANONICAL_LABELS = [
  'Preparation Started',
  'Biological Processing',
  'Quality Check',
  'Dispatched',
  'Delivered',
  'Start Tomorrow',
];

const labelSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default function PhasePreparation() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(`${API}/subscriber/formulation/phase-preparation`);
      setData(r.data);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load your phase preparation status');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  return (
    <div data-testid="subscriber-phase-preparation-page" className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center gap-3 mb-6">
          <Sparkles className="w-6 h-6 text-cyan-300" />
          <h1 className="text-2xl font-semibold">Your Phase Preparation</h1>
        </div>

        {loading && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin text-white/50" />
            <div className="text-sm text-white/60">Loading…</div>
          </div>
        )}

        {pageError && !loading && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
            <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={load}>Retry</Button>
          </div>
        )}

        {!loading && !pageError && data && !data.phase_active && (
          <EmptyState />
        )}

        {!loading && !pageError && data?.phase_active && (
          <ActiveCard data={data} reload={load} />
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div data-testid="phase-prep-empty-state" className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center">
      <PackageCheck className="w-8 h-8 mx-auto text-white/30 mb-3" />
      <div className="text-base text-white/80 mb-1">No active phase preparation right now</div>
      <p className="text-xs text-white/50 max-w-md mx-auto">
        We&rsquo;ll let you know as soon as your next phase is being prepared.
      </p>
    </div>
  );
}

function ActiveCard({ data, reload }) {
  const completed = new Set(data.completed_labels || []);
  const current = data.current_label;
  const canConfirm = !!data.can_confirm_delivery;
  const canReport = !!data.can_report_issue;
  const confirmedDelivery = !!data.subscriber_confirmed_delivery;

  const [confirming, setConfirming] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueText, setIssueText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirmDelivery = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/subscriber/formulation/confirm-delivery`);
      toast.success('Thanks — your delivery is confirmed.');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm delivery');
    } finally {
      setConfirming(false);
    }
  };

  const submitIssue = async () => {
    if (!issueText.trim()) {
      toast.error('Please tell us what happened');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscriber/formulation/report-delivery-issue`,
        { issue_text: issueText.trim() });
      toast.success('We\u2019ve recorded the issue. Our care team will follow up.');
      setIssueOpen(false);
      setIssueText('');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record issue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
      <p className="text-sm text-white/70 leading-relaxed mb-6">
        Your personalized ReGenflō phase is being prepared through our biological formulation process.
      </p>

      <div data-testid="phase-prep-progress-list" className="space-y-2 mb-6">
        {CANONICAL_LABELS.map((label) => {
          const isDone = completed.has(label);
          const isCurrent = label === current;
          const showPackedBadge = isCurrent && label === 'Quality Check' && data.packed_badge;
          return (
            <div
              key={label}
              data-testid={`phase-prep-step-${labelSlug(label)}`}
              className={`flex items-center gap-3 py-1.5 px-3 rounded-lg border
                ${isCurrent ? 'border-cyan-500/30 bg-cyan-500/5' : ''}
                ${isDone && !isCurrent ? 'border-white/5 bg-white/[0.02]' : ''}
                ${!isDone && !isCurrent ? 'border-white/5 bg-transparent' : ''}
              `}
            >
              {isDone ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              ) : isCurrent ? (
                <span className="w-2 h-2 rounded-full bg-cyan-400 ml-1 mr-1 flex-shrink-0" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-white/20 ml-1 mr-1 flex-shrink-0" />
              )}
              <span className={`text-sm ${isDone ? 'text-white/70' : isCurrent ? 'text-white' : 'text-white/40'}`}>
                {label}
              </span>
              {showPackedBadge && (
                <span className="ml-auto text-[10px] text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5 bg-emerald-500/10">
                  Packed ✓
                </span>
              )}
            </div>
          );
        })}
      </div>

      {data.generic_delay_note && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200 mb-4 flex items-start gap-2">
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{data.generic_delay_note}</span>
        </div>
      )}

      {data.expected_delivery_date && current !== 'Delivered' && current !== 'Start Tomorrow' && (
        <div className="text-xs text-white/50 mb-4">
          Expected delivery: <span className="text-white/80">{formatSoftDate(data.expected_delivery_date)}</span>
        </div>
      )}

      {data.execution_start_date && (current === 'Start Tomorrow' || current === 'Delivered') && (
        <div className="text-sm text-cyan-200 border border-cyan-500/30 bg-cyan-500/5 rounded-lg p-3 mb-4">
          Your execution starts on <span className="font-medium">{formatSoftDate(data.execution_start_date)}</span>.
        </div>
      )}

      {confirmedDelivery && (
        <div
          data-testid="phase-prep-delivery-confirmed-chip"
          className="mb-4 text-xs rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-emerald-200 flex items-center gap-2"
        >
          <CheckCircle2 className="w-4 h-4" />
          Thanks — we&rsquo;ve recorded that your package arrived.
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
        {canConfirm && (
          <Button
            data-testid="phase-prep-confirm-delivery-btn"
            onClick={confirmDelivery}
            disabled={confirming}
            className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 text-xs h-8"
          >
            {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
            Confirm Delivery
          </Button>
        )}
        {canReport && (
          <Button
            data-testid="phase-prep-report-issue-btn"
            variant="outline"
            onClick={() => setIssueOpen(true)}
            className="border-white/15 text-white/80 hover:bg-white/5 text-xs h-8"
          >
            Report Delivery Issue
          </Button>
        )}
        {!canConfirm && !canReport && (
          <p className="text-[11px] text-white/40">
            Delivery confirmation will be available once your package is on its way.
          </p>
        )}
      </div>

      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent
          data-testid="phase-prep-report-issue-modal"
          className="bg-slate-900 border border-white/10"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Report a delivery issue</DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              Our care team will review and follow up with you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-[11px] text-white/60">Tell us what happened (optional)</Label>
            <Textarea
              data-testid="phase-prep-report-issue-textarea"
              rows={4}
              maxLength={1000}
              value={issueText}
              onChange={(e) => setIssueText(e.target.value)}
              placeholder="A short description helps us help you faster…"
              className="bg-slate-950/40 border-white/10 text-sm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIssueOpen(false)}
              className="border-white/15 text-white/80"
            >
              Cancel
            </Button>
            <Button
              data-testid="phase-prep-report-issue-submit"
              onClick={submitIssue}
              disabled={submitting}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatSoftDate(iso) {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}
